import { CommonContentWrapper } from "../../common/components/content_wrapper/CommonContentWrapper";
import { Padding, Row, SizedBox } from "../../common/components/CommonBaseComponents";
import { PageTitle } from "../../common/components/PageTitle";
import { C3Text } from "../../common/components/text/CustomText";
import { useEffect, useRef, useState } from "react";
import CustomNetworkImage from "../../common/components/image/CustomNetworkImage";
import { CustomTable, LinkTd, ShopStatus } from "../../common/components/table/CustomTable";
import SearchBar from "../../common/components/input/SearchBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import SecondaryButton from "../../common/components/button/SecondaryButton";
import PrimaryButton from "../../common/components/button/PrimaryButton";
import Translate from "../../common/translate/Translate";
import CategoryFilterButton from "../../common/components/filter_button/CategoryFilterButton";
import ProvinceFilterButton from "../../common/components/filter_button/ProvinceFilterButton";
import LocalStorageKey from "../../common/enums/LocalStorageKey";
import Role from "../../common/enums/Role";
import StoreSaveModal from "./modals/StoreSaveModal";
import { useDevice } from "../../common/hooks/useDevice";
import { useWindowSize } from "../../common/hooks/useWindowSize";
import StoreService from "../../service/StoreService";

export function StoreListPage() {
    const navigation = useNavigate();
    const service = new StoreService();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get("page") || 1;
    const [provinceFilter, setProvinceFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState(["all"]);
    const [searchFilter, setSearchFilter] = useState(searchParams.get("searchType") || "Shop Code");
    const [searchValue, setSearchValue] = useState(searchParams.get("searchValue") || "");

    const role = localStorage.getItem(LocalStorageKey.role);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // API data
    const [storeList, setStoreList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const onSearch = () => {
        fetchStoreList(1, 15, searchFilter, searchValue);
    };

    const fetchStoreList = (page, pageSize, searchFilter, searchValue) => {
        // TODO: LG Admin 권한일 때 franchisesCode 필터 UI 연결 전까지 빈 값 전달
        const searchKeyMap = {
            "Franchise Code": "franchiseCode",
            "Franchise Name": "franchiseName",
            "Shop Code": "shopCode",
            "Shop Name": "shopName",
            "Phone Number": "phoneNumber",
            "Owner Name": "phoneNumber",
            Address: "address",
        };
        const query = { page, pageSize };
        if (provinceFilter !== "all") query.states = provinceFilter;
        if (searchValue !== "" && searchKeyMap[searchFilter]) query[searchKeyMap[searchFilter]] = searchValue;

        service
            .getStoreList(query)
            .then((result) => {
                if (!isMountedRef.current) return;
                const stores = result.storeInfo || [];
                setStoreList(stores);
                if (result.page) {
                    setTotalCount(result.page.totalCount || 0);
                    const existingParams = Object.fromEntries(searchParams.entries());
                    setSearchParams({ ...existingParams, totalPage: result.page.totalPage }, { replace: true });
                }
                service.getStoresImages({ stores }).then((enrichedList) => {
                    if (!isMountedRef.current) return;
                    setStoreList(enrichedList);
                });
            })
            .catch((error) => {
                console.error("Error fetching store list:", error);
            });
    };

    const syncShopCode = () => {
        service.syncShopCode().then(() => {
            fetchStoreList(page, 15);
        });
    };

    useEffect(() => {
        fetchStoreList(page, 15);
    }, [page, provinceFilter]);

    const handleSave = () => {
        fetchStoreList(page, 15);
    };

    // for table
    const tableHeaders = [
        ...(role === Role.Admin
            ? [
                {
                    key: "franchiseCode",
                    label: "Franchise Code",
                },
                {
                    key: "franchiseName",
                    label: "Franchise Name",
                },
            ]
            : []),
        {
            key: "shopCode",
            label: Translate("store.shopCode"),
            render: (row) => (
                <LinkTd
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigation(`/shops/${row.storeId}`);
                    }}
                >
                    {row.shopCode}
                </LinkTd>
            ),
        },
        {
            key: "storeName",
            label: Translate("store.shopName"),
            textAlign: "start",
            whiteSpace: "normal",
            minWidth: "388px",
            render: (row) => (
                <Row maxWidth={"388px"}>
                    <CustomNetworkImage src={row.thumbnail} width={"96px"} height={"54px"} borderRadius={"12px"} />
                    <SizedBox width={"10px"} />
                    <C3Text flex={"1"} fontWeight={"500"}>
                        {row.shopName}
                    </C3Text>
                </Row>
            ),
        },
        {
            key: "address",
            label: Translate("store.province"),
            render: (row) => row.address?.states || "-",
        },
        {
            key: "address",
            label: Translate("store.address"),
            render: (row) => {
                const addr = row.address;
                if (!addr) return "-";
                return [addr.address1, addr.address2, addr.city, addr.states].filter(Boolean).join(", ");
            },
        },
        {
            key: "contact",
            label: Translate("store.contact"),
        },
        {
            key: "ownerId",
            label: Translate("store.ownerName"),
        },
        {
            key: "status",
            label: Translate("common.status"),
            render: (row) => <ShopStatus status={row.isReservationEnable ? "Open" : "Closed"} />,
        },
        {
            key: "createDate",
            label: Translate("common.registeredDate"),
        },
    ];

    // save modal state
    const [openSaveModal, setOpenSaveModal] = useState(false);

    const currentDevice = useDevice();
    const { width } = useWindowSize();

    let addButton = (
        <PrimaryButton icon={"add_white"} text={Translate("store.addStore")} onClick={() => setOpenSaveModal(true)} />
    );
    let syncButton = <PrimaryButton icon={"add_white"} text={"sync"} onClick={() => syncShopCode()} />;
    let exportExcelButton = <SecondaryButton icon={"download"} text={Translate("common.exportExcel")} />;
    let pageTitle = <PageTitle title={Translate("gnb.storeList")} contentLength={totalCount} />;

    if (currentDevice === "mobile" || currentDevice === "tablet" || width <= 1125) {
        addButton = <PrimaryButton icon={"add_white"} onClick={() => setOpenSaveModal(true)} onlyIcon />;
        exportExcelButton = <></>;
    }
    if (currentDevice === "mobile" || currentDevice === "tablet") {
        pageTitle = (
            <PageTitle
                title={Translate("gnb.storeList")}
                contentLength={totalCount}
                button={
                    (currentDevice === "mobile" || currentDevice === "tablet") && role === Role.StoreManager
                        ? addButton
                        : undefined
                }
            />
        );
    }

    const searchBar = (
        <SearchBar
            type={"storeList"}
            totalWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "100%" : undefined}
            selectorWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "20%" : undefined}
            inputWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "70%" : undefined}
            isAdmin={role === Role.Admin}
            currentValue={searchFilter}
            setValue={setSearchFilter}
            setSearchValue={setSearchValue}
            onSearchButtonClick={onSearch}
        />
    );
    const filterButtons = (
        <>
            <ProvinceFilterButton currentValue={provinceFilter} setValue={setProvinceFilter} />
            <SizedBox width={"10px"} />
            <CategoryFilterButton currentValues={statusFilter} setValues={setStatusFilter} type={"storeList"} />
        </>
    );

    let header = (
        <>
            {pageTitle}
            <SizedBox height={"24px"} />
            <Row justifyContent={"space-between"}>
                <Row>
                    {searchBar}
                    <SizedBox width={"10px"} />
                    {filterButtons}
                </Row>
                <Row>
                    {role === Role.StoreManager && addButton}
                    {syncButton}
                    <SizedBox width={"10px"} />
                    {exportExcelButton}
                    {role !== Role.Admin && (
                        <>
                            <SizedBox width={"13px"} />
                        </>
                    )}
                </Row>
            </Row>
        </>
    );

    if (currentDevice === "mobile" || currentDevice === "tablet") {
        header = (
            <>
                {pageTitle}
                <SizedBox height={"20px"} />
                {searchBar}
                <SizedBox height={"12px"} />
                <Row>{filterButtons}</Row>
            </>
        );
    }

    return (
        <CommonContentWrapper hasPagination={true}>
            <Padding padding={"24px 20px"}>
                {header}
                <SizedBox height={"20px"} />
                <CustomTable headers={tableHeaders} data={storeList} />
            </Padding>
            {openSaveModal && (
                <StoreSaveModal
                    isOpen={openSaveModal}
                    onClose={() => setOpenSaveModal(false)}
                    onSave={handleSave}
                ></StoreSaveModal>
            )}
        </CommonContentWrapper>
    );
}
